import { onElementExistRunOnce } from 'cropwise_accounts/helpers/dom_utils';
import { initAvailableProductsCardIcons } from './internal/available_products_card_icons';
import { initAvailableProductsModal } from './internal/available_products_modal';
import { initAvailableProductsSubmitGuard } from './internal/available_products_submit_guard';
import { initClientIdParam } from './internal/client_id_param';
import { initCookieBannerCleanup } from './internal/cookie_banner_cleanup';
import { initCookieSettings } from './internal/cookie_settings';
import { initLanguageSelector } from './internal/language_selector';
import { initLegaldocLinks } from './internal/legaldoc_links';
import { initCountryFlag } from './internal/country_flag';
import { initDepartmentJobTitlePicker } from './internal/department_job_title_picker';
import { initPhoneDialCode } from './internal/phone_dial_code';
import { initLoginErrorModal } from './internal/login_error_modal';
import { initPasswordToggle } from './internal/password_toggle';
import { initPasswordStrength } from './internal/password_strength';
import { initPasswordConfirmationMatch } from './internal/password_confirmation_match';
import { initProductCardIcons } from './internal/product_card_icons';
import { initWorkspaceSelector } from './internal/workspace_selector';
import { initFormSubmitGuard } from './internal/form_submit_guard';
import { initSignupFieldEvents } from './internal/signup_field_events';
import { initNameCapitalization } from './internal/name_capitalization';
import { initSignupStepTransition } from './internal/signup_step_transition';
import { initRequiredFieldsGate } from './internal/required_fields_gate';
import { initCampaignOrigin } from './internal/campaign_origin';
import { initTrackAuth } from './internal/track_auth';
import { initPasswordRecoveryEvents } from './internal/password_recovery_events';
import { initLegalDocsGate } from './internal/legal_docs_gate';
import { initTwoFactorCountdown } from './internal/two_factor_countdown';
import { initTwoFactorCodeInput } from './internal/two_factor_code_input';
import { initTwoFactorEvents } from './internal/two_factor_events';
import { initMfaNoticeEvents } from './internal/mfa_notice_events';
import { initUserErrorEvents } from './internal/user_error_events';
import { initLegalDocumentsViewer } from './internal/legal_documents_viewer';
import { initLegalDocumentsDrawer } from './internal/legal_documents_drawer';
import { initRecaptchaGuard } from './internal/recaptcha_guard';
import { initNativeAppHandoff } from './internal/native_app_handoff';
import { initChannelOptions } from './internal/channel_options';
import { initPasswordRecoveryOtpCodeInput } from './internal/password_recovery_otp_code_input';
import { initPasswordRecoveryOtpCountdown } from './internal/password_recovery_otp_countdown';

/**
 * Orchestrates initialization of all Cropwise Accounts login layout concerns:
 * cookie consent banner cleanup, cookie settings, language selection,
 * legal-document link sync, login error
 * modal, password toggle, password strength meter, confirm-password match
 * feedback, the product-overview icon fallback, the sign-up
 * field-interaction Amplitude events, the sign-up name-field
 * capitalization, and the sign-up password-to-profile step transition.
 */
const initCropwiseAccountsLoginLayout = () => {
  onElementExistRunOnce('.cropwise-accounts-login-layout', () => {
    // Attached first so its submit listener runs — and, on the intermediate
    // password->profile transition, calls preventDefault — before any other
    // submit listener on #registration-form (phone dial-code reset, "user
    // sign-up" tracking) that must not fire until the real, final submit.
    initSignupStepTransition();
    initClientIdParam();
    initNativeAppHandoff();
    initCookieBannerCleanup();
    initCookieSettings();
    initLanguageSelector();
    initLegaldocLinks();
    initCountryFlag();
    initDepartmentJobTitlePicker();
    initPhoneDialCode();
    initLoginErrorModal();
    initPasswordToggle();
    initPasswordStrength();
    initPasswordConfirmationMatch();
    initProductCardIcons();
    initFormSubmitGuard();
    initRecaptchaGuard();
    initSignupFieldEvents();
    initNameCapitalization();
    initRequiredFieldsGate();
    initCampaignOrigin();
    initTrackAuth();
    initPasswordRecoveryEvents();
    initLegalDocsGate();
    initTwoFactorCountdown();
    initTwoFactorCodeInput();
    initTwoFactorEvents();
    initUserErrorEvents();
    initChannelOptions();
    initPasswordRecoveryOtpCodeInput();
    initPasswordRecoveryOtpCountdown();
    initLegalDocumentsDrawer();
  });

  // The legal-documents page has its own minimal layout (no login shell), so it
  // only wires the consent gate and the "user accept legal docs" tracking —
  // running the full login init here would trip login-only concerns.
  onElementExistRunOnce('.cropwise-accounts-legal-layout', () => {
    initLegalDocsGate();
    initTrackAuth();
  });

  // The change-idp/mfa-notice popup layout has no marketing shell, but the
  // MFA notice phone form reuses the signup country/dial-code selector.
  onElementExistRunOnce('.cropwise-accounts-change-idp-layout', () => {
    initCountryFlag();
    initPhoneDialCode();
    initMfaNoticeEvents();
  });

  // The product-overview layout has its own grey canvas (no login shell); it
  // only needs the language selector and the product-card icon fallback.
  onElementExistRunOnce('.cropwise-accounts-product-layout', () => {
    initLanguageSelector();
    initProductCardIcons();
    initLegalDocumentsDrawer();
  });

  // The available-products layout shares the grey product canvas but reloads on
  // workspace change to re-render the CURRENT-vs-NEW plan comparison.
  onElementExistRunOnce('.cropwise-accounts-available-products-layout', () => {
    initLanguageSelector();
    initWorkspaceSelector();
    initAvailableProductsCardIcons();
    initAvailableProductsModal();
    initAvailableProductsSubmitGuard();
    initLegalDocumentsDrawer();
  });

  // The legal-documents viewer has its own minimal layout (no login shell,
  // no footer) — it only needs its own content behavior wired up.
  onElementExistRunOnce('.cropwise-accounts-legal-viewer-layout', () => {
    initLanguageSelector(document, '#legal-documents-viewer-language-selector');
    initLegalDocumentsViewer();
  });
};

export default initCropwiseAccountsLoginLayout;
